import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { LoadScript, Autocomplete } from "@react-google-maps/api";

// Move libraries outside to prevent performance warnings
const GOOGLE_MAPS_API_KEY = "AIzaSyAnVLYPFx51uzZQ8C8eD7Owt05eeOFGF78";
const libraries = ["places"];

const AddressBookingModal = ({
  showModal,
  setShowModal,
  bookingId,
  userId,
  onAddressSubmit,
  initialAddress
}) => {
  const [formData, setFormData] = useState({
    fullAddress: "",
    locality: "",
    direction: "",
    lattitude: "",
    longitude: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (showModal) {
      // Reset form data when modal is opened
      setFormData({
        fullAddress: initialAddress?.fullAddress || "",
        locality: initialAddress?.locality || "",
        direction: initialAddress?.direction || "",
        lattitude: initialAddress?.lattitude || "",
        longitude: initialAddress?.longitude || "",
      });
      setErrors({});
    }
  }, [showModal, initialAddress]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    // Clear any error for this field
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    }
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.formatted_address) {
      setFormData((prev) => ({ ...prev, fullAddress: place.formatted_address }));
      setErrors((prev) => ({ ...prev, fullAddress: "" })); // Clear error on valid selection
    }
    
    if (place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData((prev) => ({ ...prev, lattitude: lat, longitude: lng }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullAddress) newErrors.fullAddress = "Address is required";
    if (!formData.locality) newErrors.locality = "Locality is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const payload = { 
        ...formData, 
        userId: userId,
        bookingId: bookingId 
      };
      
      await onAddressSubmit(payload);
      
      setShowModal(false);
      setFormData({
        fullAddress: "",
        locality: "",
        direction: "",
        lattitude: "",
        longitude: "",
      });
      
      //toast.success("Address added successfully");
    } catch (error) {
      console.error("Error submitting address:", error);
      toast.error("Failed to submit address");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          {/* Modal Content */}
          <div className="bg-white w-1/2 p-6 rounded shadow-lg">
            <h4 className="text-lg font-semibold mb-4">Add Customer Address</h4>
            <p className="mb-4 text-gray-600">Please provide the customer's address details for this booking.</p>
            
            <div className="mb-4">
              <label htmlFor="fullAddress" className="block text-gray-700 font-medium mb-2">
                House/Flat/Block No. <span className="text-red-500">*</span>
              </label>
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <Autocomplete 
                  onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} 
                  onPlaceChanged={handlePlaceSelect}
                >
                  <input
                    type="text"
                    name="fullAddress"
                    value={formData.fullAddress}
                    onChange={handleInputChange}
                    placeholder="Enter full address"
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </Autocomplete>
              </LoadScript>
              {errors.fullAddress && <p className="text-red-500 text-sm mt-1">{errors.fullAddress}</p>}
            </div>

            <div className="mb-4">
              <label htmlFor="locality" className="block text-gray-700 font-medium mb-2">
                Apartment/Road/Area <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="locality"
                value={formData.locality}
                onChange={handleInputChange}
                placeholder="Enter locality"
                className="w-full p-2 border border-gray-300 rounded"
              />
              {errors.locality && <p className="text-red-500 text-sm mt-1">{errors.locality}</p>}
            </div>

            <div className="mb-4">
              <label htmlFor="direction" className="block text-gray-700 font-medium mb-2">
                Directions To Reach
              </label>
              <textarea
                name="direction"
                value={formData.direction}
                onChange={handleInputChange}
                placeholder="Enter additional directions if any"
                className="w-full p-2 border border-gray-300 rounded"
                rows="3"
              />
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Update Address"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddressBookingModal;
